'use client'

import { ReactSelectMenu } from '@repo/ui/ReactSelectMenu'
import React, { useEffect, useState, useRef } from 'react'
import { useEmailCardStore } from '@repo/store/useEmailCardStore'
import { useDebounce } from '@uidotdev/usehooks'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIStack from '@mui/material/Stack'

export const EmailSidebarEmailTemplateCard = ({ testData = false }: EmailSidebarEmailTemplateCardProps) => {
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<any[]>([])
  const [pageSize] = useState(25)
  const [searchTerm, setSearchTerm] = useState(null)
  const [startAtIndex, setStartAtIndex] = useState(0)
  const [totalRowCount, setTotalRowCount] = useState(0)
  const [workTypeId, setWorkTypeId] = useState('GLOBAL')
  const debouncedStartAtIndex = useDebounce(startAtIndex, 500)
  const debouncedSearchTerm = useDebounce(searchTerm, 500)

  const {
    emailSendingCurrentMatterData,
    fetchEmailTemplates,
    fetchSingleEmailTemplate,
    selectedEmailTemplate,
    selectedEmailTemplatePosition,
    setSelectedEmailTemplate,
    setSelectedEmailTemplatePosition
  } = useEmailCardStore()

  const handleScroll = (e: any) => {
    const target = e.target as HTMLDivElement
    const atBottom = target.scrollTop + target.clientHeight >= target.scrollHeight
    const more = options.length < totalRowCount

    if (atBottom && more) {
      setStartAtIndex(startAtIndex + pageSize)
    }
  }

  useEffect(() => {
    if (testData) return

    const fetchData = async () => {
      setLoading(true)

      let rows: any[] = []

      const emailTemplates = await fetchEmailTemplates({
        name: debouncedSearchTerm,
        workTypeId: emailSendingCurrentMatterData.workTypeId,
        startAtIndex: debouncedStartAtIndex,
        pageSize: pageSize
      })

      if (debouncedStartAtIndex !== 0) {
        rows = [...options, ...emailTemplates.rows]
      } else {
        rows = emailTemplates?.rows
      }

      setOptions(rows)
      setLoading(false)
    }

    fetchData()
  }, [testData, debouncedStartAtIndex])

  useEffect(() => {
    if (testData) return

    const fetchNewData = async () => {
      setLoading(true)
      setStartAtIndex(0)
      setOptions([])
      setTotalRowCount(0)
      setSelectedEmailTemplate('')

      const result = await fetchEmailTemplates({
        name: debouncedSearchTerm,
        workTypeId: emailSendingCurrentMatterData.workTypeId,
        startAtIndex: startAtIndex,
        pageSize: pageSize
      })

      setOptions(result?.rows ?? [])
      setTotalRowCount(result?.totalRowCount ?? 0)
      setWorkTypeId(emailSendingCurrentMatterData.workTypeId)
      setLoading(false)
    }

    fetchNewData()
  }, [emailSendingCurrentMatterData.workTypeId, testData, debouncedSearchTerm])

  useEffect(() => {
    if (debouncedSearchTerm === '') {
      setSearchTerm(null)
    }
  }, [debouncedSearchTerm])

  const onChange = async (newValue: any) => {
    if (
      newValue &&
      selectedEmailTemplate &&
      newValue.emailTemplateId &&
      selectedEmailTemplate.emailTemplateId &&
      selectedEmailTemplate.emailTemplateId === newValue.emailTemplateId
    ) {
      // value hasn't changed
      return
    }

    newValue &&
      (await fetchSingleEmailTemplate({
        emailTemplateId: newValue.emailTemplateId,
        clientNo: emailSendingCurrentMatterData?.clientNo,
        matterNo: emailSendingCurrentMatterData?.matterNo
      }))

    setSelectedEmailTemplate(newValue)
    return newValue
  }

  return (
    <MUIStack data-testid="EmailSidebarEmailTemplateCard" id="emailTemplate">
      <MUIRadioGroup
        name="selectedEmailTemplatePosition"
        value={selectedEmailTemplatePosition}
        onChange={(e) => setSelectedEmailTemplatePosition(e.target.value)}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          mb: 2,
          width: '100%'
        }}
      >
        <MUIFormControlLabel value="append" control={<MUIRadio />} label="Append" sx={{ mr: 10 }} />
        <MUIFormControlLabel value="replace" control={<MUIRadio />} label="Replace" />
      </MUIRadioGroup>

      <ReactSelectMenu
        valueKey="emailTemplateId"
        labelKey="name"
        items={options}
        label="Email Template"
        loading={loading}
        multi={false}
        name="emailTemplateSelect"
        showIdInResults={false}
        // @ts-ignore
        onChange={onChange}
        required={false}
        size={'small'}
        sx={{ mb: 4 }}
        menuZindex={9999999}
        onSearchTermChange={setSearchTerm}
        // @ts-ignore
        value={selectedEmailTemplate ?? null}
        // @ts-ignore
        onAtBottom={handleScroll}
        menuWidth={'100%'}
      />
    </MUIStack>
  )
}

type EmailSidebarEmailTemplateCardProps = {
  testData?: any
}
